import {
  SegmentApiItem,
  SegmentTableData,
  SegmentFilterGroup,
  SegmentFilterCondition,
  SegmentLeadItem,
  SegmentFilterGroupDisplay,
} from "../types/segments.types";
import type {
  FilterConditionUI,
  FilterGroupUI,
} from "../../../../Crm/components/KanbanGrid/utlis/crmViewConstants";
import {
  CRM_FILTER_FIELD_OPERATORS,
  CRM_VIEW_FILTER_FIELD_OPTIONS,
  CRM_SINGLE_VALUE_FIELDS,
  CRM_VALUE_OPTIONS_MAP,
  DURATION_OPTIONS,
  budgetKeyToPayload,
  leaseDurationKeyToPayload,
  budgetPayloadToKey,
  leaseDurationPayloadToKey,
} from "../../../../Crm/components/KanbanGrid/utlis/crmViewConstants";
import {
  SEGMENT_EXTRA_SINGLE_VALUE_FIELDS,
  SEGMENT_EXTRA_VALUE_OPTIONS,
  SEGMENT_EXTRA_FILTER_FIELDS,
  SEGMENT_EXTRA_OPERATORS,
  SEGMENT_DEAL_NUMBER_FIELDS,
  SEGMENT_DEAL_DATE_FIELDS,
  SEGMENT_DEAL_BOOLEAN_FIELDS,
  SEGMENT_DEAL_ARRAY_FIELDS,
  SEGMENT_LEAD_ARRAY_FIELDS,
  SEGMENT_FREE_TEXT_FIELDS,
} from "../constants/segments.constants";
import { Lead } from "../../../../Crm/utils/types";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";

function prettifyPresetValue(value: string): string {
  if (!value || !value.includes("_")) return value;
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const transformSegmentApiItem = (
  item: SegmentApiItem
): SegmentTableData => ({
  id: item.id,
  name: item.name,
  size: item.cached_size,
  type: item.segment_type,
  last_updated: item.updated_at,
  created_by: item.created_by_name || item.created_by,
  created_on: item.created_at,
});

const ALL_SINGLE_VALUE_FIELDS = [
  ...CRM_SINGLE_VALUE_FIELDS,
  ...SEGMENT_EXTRA_SINGLE_VALUE_FIELDS,
];

const INTEGER_ARRAY_FIELDS = ["destination_country", "destination_city", "property"];
const INTEGER_SINGLE_FIELDS = ["university"];

const ALWAYS_SINGLE_STRING_OPS = new Set([
  "equals",
  "not_equals",
  "contains",
  "not_contains",
  "before",
  "after",
  "greater_than",
  "less_than",
]);

// `not_in` and `contains_any` semantically require a list — always emit array.
const ARRAY_VALUE_OPS = new Set(["not_in", "contains_any"]);

// Lead-side BOOLEAN fields per catalogue. Must coerce "true"/"false" strings
// to real booleans before sending.
const LEAD_BOOLEAN_FIELDS = [
  "viewings",
  "exclude_repeat_leads",
  "marketing_consent",
];

function toNumberOrUndefined(raw: string | undefined): number | undefined {
  if (raw == null || raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function getSegmentConditionValue(
  key: string,
  value: string[],
  comparisonOperator?: string
) {
  if (key === "budget") {
    const payload = budgetKeyToPayload(value[0], "GBP");
    if (payload && typeof payload === "object" && "min_budget" in payload) {
      return { ...payload, duration: "monthly" };
    }
    return payload;
  }
  if (key === "lease_duration") return leaseDurationKeyToPayload(value[0]);
  if (comparisonOperator === "exists") return value[0] === "true";

  // Lead `duration` field — DurationPopper stores [start_iso, end_iso, optionId];
  // strip the optionId before sending.
  if (key === "duration" && comparisonOperator === "range") {
    const start = value[0];
    const end = value[1];
    return start && end ? [start, end] : [];
  }

  if (
    SEGMENT_DEAL_BOOLEAN_FIELDS.includes(key) ||
    LEAD_BOOLEAN_FIELDS.includes(key)
  ) {
    return value[0] === "true";
  }

  if (SEGMENT_DEAL_NUMBER_FIELDS.includes(key)) {
    if (comparisonOperator === "range") {
      const min = toNumberOrUndefined(value[0]);
      const max = toNumberOrUndefined(value[1]);
      const out: { min?: number; max?: number } = {};
      if (min !== undefined) out.min = min;
      if (max !== undefined) out.max = max;
      return out;
    }
    return toNumberOrUndefined(value[0]) ?? value[0];
  }

  if (SEGMENT_DEAL_DATE_FIELDS.includes(key)) {
    if (comparisonOperator === "range") {
      // DurationPopper stores [start_iso, end_iso, optionId]; only send the
      // two iso strings.
      const start = value[0];
      const end = value[1];
      if (start && end) return [start, end];
      return [];
    }
    // single-date ops produce a "YYYY-MM-DD" string from the native input.
    return value?.[0] ?? "";
  }

  // Multi-value ops (not_in / contains_any) always send arrays, regardless of
  // whether the field uses a single-select UI or is a single-value field.
  if (comparisonOperator && ARRAY_VALUE_OPS.has(comparisonOperator)) {
    if (
      INTEGER_ARRAY_FIELDS.includes(key) ||
      INTEGER_SINGLE_FIELDS.includes(key)
    ) {
      return value.map((v) => parseInt(v, 10)).filter((n) => !isNaN(n));
    }
    return value;
  }
  // Free-text fields send a single string on `contains` / `not_contains`
  // instead of an array, matching their free-text UI input.
  if (
    (comparisonOperator === "contains" ||
      comparisonOperator === "not_contains") &&
    SEGMENT_FREE_TEXT_FIELDS.includes(key)
  ) {
    return value?.[0] ?? "";
  }
  if (INTEGER_ARRAY_FIELDS.includes(key))
    return value.map((v) => parseInt(v, 10)).filter((n) => !isNaN(n));
  if (INTEGER_SINGLE_FIELDS.includes(key)) {
    const num = parseInt(value[0], 10);
    return isNaN(num) ? value[0] : num;
  }
  // Lead ARRAY fields per catalogue — always emit string[] regardless of
  // whether the UI uses a single-select or multi-select widget.
  if (SEGMENT_LEAD_ARRAY_FIELDS.includes(key)) {
    return value;
  }
  // Deal-side array fields use multi-select for `contains` / `not_contains` —
  // send the full array under the same operator key. (`not_in` is already
  // handled by the ARRAY_VALUE_OPS branch above.)
  if (
    (comparisonOperator === "contains" ||
      comparisonOperator === "not_contains") &&
    SEGMENT_DEAL_ARRAY_FIELDS.includes(key)
  ) {
    return value;
  }
  // Catalogue: equals / not_equals / contains / not_contains are single string.
  if (
    comparisonOperator &&
    ALWAYS_SINGLE_STRING_OPS.has(comparisonOperator) &&
    !ALL_SINGLE_VALUE_FIELDS.includes(key)
  ) {
    return value?.[0] ?? "";
  }
  if (ALL_SINGLE_VALUE_FIELDS.includes(key)) return value?.[0] ?? "";
  return value;
}

function resolveObjectType(c: FilterConditionUI): "lead" | "deal" {
  if (c.object_type === "deal") return "deal";
  if (c.object_type === "lead") return "lead";
  return c.key?.startsWith("deal_") ? "deal" : "lead";
}

// Skip rows where key, operator, or value is empty — never send incomplete
// filters to BE. Range needs BOTH endpoints; other ops need at least one value.
function isConditionComplete(c: FilterConditionUI): boolean {
  if (!c?.key || !c?.comparison_operator) return false;

  const values = c.value ?? [];
  if (c.comparison_operator === "range") {
    return Boolean(values[0]) && Boolean(values[1]);
  }
  return values.some(Boolean);
}

function toApiCondition(c: FilterConditionUI) {
  return {
    key: c.key,
    comparison_operator: c.comparison_operator,
    value: getSegmentConditionValue(c.key, c.value, c.comparison_operator),
    is_custom_field: c.is_custom_field,
    object_type: resolveObjectType(c),
  };
}

export function mapFilterGroupsToApi(groups: FilterGroupUI[]) {
  return groups
    .map((g) => ({
      conditions: g.conditions.filter(isConditionComplete).map(toApiCondition),
      logical_operator: g.logical_operator,
    }))
    .filter((g) => g.conditions.length > 0);
}

/**
 * AND group validator — the backend rejects two AND conditions on the same
 * `deal_*` key in one group (400). Returns true if any such conflict exists.
 */
export function apiFilterGroupsToUI(
  groups: SegmentFilterGroup[]
): FilterGroupUI[] {
  return groups.map((g) => ({
    logical_operator: g.logical_operator as "and" | "or",
    conditions: g.conditions.map((c: SegmentFilterCondition) => {
      let uiValue: string[];
      const rawValue = c.value;
      if (
        c.key === "budget" &&
        rawValue &&
        typeof rawValue === "object" &&
        !Array.isArray(rawValue)
      ) {
        uiValue = [
          budgetPayloadToKey(
            rawValue as unknown as Parameters<typeof budgetPayloadToKey>[0]
          ),
        ];
      } else if (
        c.key === "lease_duration" &&
        rawValue &&
        typeof rawValue === "object" &&
        !Array.isArray(rawValue)
      ) {
        uiValue = [
          leaseDurationPayloadToKey(
            rawValue as unknown as Parameters<typeof leaseDurationPayloadToKey>[0]
          ),
        ];
      } else if (
        SEGMENT_DEAL_NUMBER_FIELDS.includes(c.key) &&
        c.comparison_operator === "range" &&
        rawValue &&
        typeof rawValue === "object" &&
        !Array.isArray(rawValue)
      ) {
        const obj = rawValue as { min?: number; max?: number };
        uiValue = [
          obj.min != null ? String(obj.min) : "",
          obj.max != null ? String(obj.max) : "",
        ];
      } else if (
        SEGMENT_DEAL_DATE_FIELDS.includes(c.key) &&
        c.comparison_operator === "range" &&
        Array.isArray(rawValue)
      ) {
        uiValue = (rawValue as unknown[]).map((v) => String(v));
      } else if (Array.isArray(rawValue)) {
        uiValue = rawValue.map((v) => String(v));
      } else if (rawValue == null) {
        uiValue = [];
      } else if (typeof rawValue === "boolean") {
        uiValue = [String(rawValue)];
      } else {
        uiValue = [String(rawValue)];
      }
      return {
        key: c.key,
        comparison_operator: c.comparison_operator,
        value: uiValue,
        is_custom_field: c.is_custom_field,
        object_type:
          c.object_type ?? (c.key?.startsWith("deal_") ? "deal" : "lead"),
      };
    }),
  }));
}

const countryDisplayNames = new Intl.DisplayNames(["en"], { type: "region" });

function resolveValueLabel(
  key: string,
  value: string,
  dynamicLabelMap?: Record<string, Record<string, string>>
): string {
  const staticOptions =
    CRM_VALUE_OPTIONS_MAP[key] ||
    SEGMENT_EXTRA_VALUE_OPTIONS[key] ||
    (key === "duration" ? DURATION_OPTIONS : null);

  if (staticOptions) {
    const matched = staticOptions.find((opt) => opt.key === value)?.label;
    if (matched) return matched;
  }

  const dynamicLabels = dynamicLabelMap?.[key];
  if (dynamicLabels?.[value]) {
    return dynamicLabels[value];
  }

  if (key === "source_country") {
    try {
      return countryDisplayNames.of(value) ?? value;
    } catch {
      return value;
    }
  }

  if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(value)) {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return getDateTimeByFormat(parsed).date || value;
    }
  }

  return prettifyPresetValue(value);
}

export function filterGroupsUIToPanel(
  groups: FilterGroupUI[],
  dynamicLabelMap?: Record<string, Record<string, string>>
): SegmentFilterGroupDisplay[] {
  return groups.map((g) => ({
    conditions: g.conditions.map((c) => {
      const fieldLabel =
        CRM_VIEW_FILTER_FIELD_OPTIONS.find((f) => f.key === c.key)?.label ??
        SEGMENT_EXTRA_FILTER_FIELDS.find((f) => f.key === c.key)?.label ??
        c.key;
      const opLabel =
        CRM_FILTER_FIELD_OPERATORS[c.key]?.find(
          (o) => o.key === c.comparison_operator
        )?.label ??
        SEGMENT_EXTRA_OPERATORS[c.key]?.find(
          (o) => o.key === c.comparison_operator
        )?.label ??
        c.comparison_operator;

      const resolvedValues = c.value.map((v) =>
        resolveValueLabel(c.key, v, dynamicLabelMap)
      );

      return {
        field: fieldLabel,
        operator: opLabel.toLowerCase(),
        values: resolvedValues,
      };
    }),
  }));
}

export function transformLeadItemToLead(item: SegmentLeadItem): Lead {
  return {
    lead_id: item.lead_id,
    name: item.name || "",
    email: item.email || "",
    phone: item.phone || "",
    phone_country_code: item.phone_country_code || "",
    created_at: item.created_at || "",
    updated_at: item.updated_at || item.created_at || "",
    original_lead_id: item.original_lead_id ?? null,
    move_in_date: item.move_in_date ?? null,
    current_stage: item.current_stage || "",
    lost_primary_reason: item.lost_primary_reason ?? null,
    lost_reason: item.lost_reason ?? null,
    reason_text: item.reason_text ?? null,
    budget: item.budget ?? null,
    room_type: item.room_type ?? [],
    agent_override: item.agent_override ?? false,
    tenant_type: item.tenant_type ?? null,
    property: Array.isArray(item.property) ? item.property.join(", ") : "",
    university: item.university ?? null,
    source_country: item.source_country ?? null,
    destination_country: item.destination_country ?? "",
    destination_city: item.destination_city ?? "",
    destination_country_alpha2: "",
    source_country_alpha2: item.source_country_alpha2 ?? null,
    source_city: item.source_city ?? null,
    tags: [],
    inbound_channels: item.inbound_channels ?? {},
    outbound_channels: item.outbound_channels ?? {},
    has_inbound_messages: item.has_inbound_messages ?? false,
    has_outbound_messages: item.has_outbound_messages ?? false,
    agent: Array.isArray(item.agent) ? item.agent : [],
    total_tasks_count: item.total_tasks_count ?? 0,
    overdue_tasks_count: item.overdue_tasks_count ?? 0,
    lead_score: "0",
    lease: { value: "", unit: "" },
    level_of_study: item.level_of_study || "",
    notes_count: item.notes_count ?? 0,
    overdue_task_count: item.overdue_tasks_count ?? 0,
    task_count: item.total_tasks_count ?? 0,
    session_item_count: {
      call: 0,
      widget: 0,
      email: 0,
      whatsapp: 0,
      sms: 0,
      fb_messenger: 0,
      wechat: 0,
      instagram: 0,
      ...(item.session_item_count ?? {}),
    },
    category: "",
    category_fields: { customer: {} },
    country_code: "",
    cost_currency: null,
    whatsapp_phone: item.whatsapp_phone ?? null,
    whatsapp_phone_country_code: item.whatsapp_phone_country_code || "",
    same_as_phone: true,
    lead_cost: {
      widget: 0,
      call: 0,
      email: 0,
      whatsapp: 0,
      fb_messenger: 0,
      wechat: 0,
      instagram: 0,
    },
    university_id: null,
    destination_city_id: null,
    destination_country_id: null,
    channel_communication_enabled: null,
    enabled_channels: {
      whatsapp: false,
      email: false,
      call: false,
      widget: false,
    },
    outbound_tracking_info: {
      campaign_metadata: [],
      channel_tracking: { whatsapp: false },
    },
    phone_verified: false,
    campaign_lead_details: [],
    campaign_summary: null,
    lead_ip: null,
    campaign_metadata: [],
  } as Lead;
}
